import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/capacitor';
import {
  IonFooter,
  IonContent,
  IonButton,
  IonIcon,
  IonTextarea,
  IonPage,
  IonThumbnail,
  IonItem,
  IonLabel,
  useIonActionSheet,
} from '@ionic/react';
import { closeCircle, videocam } from 'ionicons/icons';
import {
  CircularProgress, Alert, Stack, Typography, Box,
} from '@mui/material';
import toast from 'react-hot-toast';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import useFetch, { CachePolicies } from 'use-http';
import { nonTerminalErrorMessages } from 'hooks/helpers/usePhotoGallery_helpers';
import {
  feedSelector, updateFeedFilters, updatePostComments, setShouldRestoreFeedPosition, updatePostSubComments,
} from '../redux/slices/feed';
import { displayName } from '../helpers/textManipulation';
import Comment from '../components/Comment';
import './Comments.css';
import { useAuthContext } from '../providers/auth-provider';
import { usePhotoGallery } from '../hooks/usePhotoGallery';
import PageWithBackButton from '../components/generic/PageWithBackButton';
import Post from '../components/Post';
import SendIconAvatar from '../components/SendIconAvatar';
import { useAppState } from '../providers/app-state/app-state-provider';
import { getMediaActionButtons, getMediaType } from '../helpers/comments';
import { displayAttachmentLabel, displayErrorText } from '../helpers/media';
import appStrings from '../common/app_strings';
import appColors from '../common/app_colors';
import BackrsButton from '../common/atoms/backrsButton/BackrsButton';
import useWindowDimensions from '../helpers/hooks';
import {
  textAreaStyles,
  postBtnStyle,
  iconContainer,
  mediaIcon,
} from './commentsStyles';

const { comments } = appStrings;
const { container, text } = appColors;

const Comments = () => {
  const { id } = useParams();
  const {
    control, handleSubmit, reset, watch,
  } = useForm();
  const watchBody = watch('body', '');
  const { user } = useAuthContext();
  const {
    takePhoto, takeVideo, takeWebVideo, loading, error, controller, takePdf,
  } = usePhotoGallery();
  const history = useHistory();
  const { width } = useWindowDimensions();
  const [post, setPost] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [media, setMedia] = useState(null);
  const [isVideo, setIsVideo] = useState(null);
  const [actionSheetResult, setActionSheetResult] = useState(null);
  const [isPosting, setIsPosting] = useState(false);
  const isAllowedToComment = post?.can_comment;
  const [editingComment, setEditingComment] = useState(null);

  const dispatch = useDispatch();
  const { feedFilters } = useSelector(feedSelector);

  const [present] = useIonActionSheet();

  const {
    get,
    response: postResponse,
  } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  });

  const { platform } = useAppState();

  const {
    post: postComment,
    put: updateComment,
    delete: deleteComment,
    loading: isLoadingCommentPost,
  } = useFetch('/comments', { cachePolicy: CachePolicies.NO_CACHE });

  const isDisabled = !isAllowedToComment || isPosting || isLoadingCommentPost || (!media?.webviewPath && (!watchBody || watchBody.trim() === ''));

  const resetReplyTo = () => {
    setReplyTo({ data: post, type: 'post' });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const fetchedPost = await get(`/posts/${id}`);
      if (cancelled) return;
      if (postResponse.ok) {
        setPost(fetchedPost);
        setReplyTo({ data: fetchedPost, type: 'post' });
      } else {
        toast.error(comments.postUnavailable, { id: 'postError' });
        history.replace('/tabs/feed');
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const reportComment = (commentId) => {
    history.replace(`/comments/${commentId}/report`, { id });
  };

  const deleteSingleComment = async (comment) => {
    const removedComment = await deleteComment(`/${comment?.id}`);

    if (removedComment?.comment_id) {
      const updatedComments = post?.comments?.map((oneComment) => {
        if (oneComment?.id === removedComment?.comment_id) {
          return {
            ...oneComment,
            comments: oneComment?.comments.filter(
              (singleComment) => singleComment?.id !== removedComment?.id,
            ),
          };
        }
        return oneComment;
      });
      setPost({ ...post, comments: updatedComments });
      dispatch(updatePostSubComments({ action: 'delete', comment: removedComment }));
    } else {
      setPost({
        ...post,
        comments: post?.comments.filter(
          (singleComment) => singleComment?.id !== removedComment?.id,
        ),
      });
      dispatch(updatePostComments({ action: 'delete', comment: removedComment }));
    }
  };

  const resetMediaState = () => {
    setMedia(null);
    setIsVideo(null);
    setActionSheetResult(null);
  };

  const handleMediaClose = () => {
    if (loading) controller.abort();
    resetMediaState();
  };

  const handleResetMedia = () => {
    handleMediaClose();
    setIsPosting(false);
  };

  const handleEditComment = (comment) => {
    setEditingComment(comment);
    reset({ body: comment.body });
    if (comment.attachments && comment.attachments.length > 0) {
      const attachment = comment.attachments[0];
      const mediaType = getMediaType(attachment?.s3_key);
      const webviewPath = mediaType !== 'pdfs' ? attachment?.uri : { name: attachment?.name, uri: attachment?.uri };
      setMedia({
        webviewPath,
        id: attachment?.id,
        type: mediaType,
      });
      setIsVideo(mediaType === 'videos');
    } else {
      handleMediaClose();
    }
  };

  const handleEditSubmit = async (data) => {
    setIsPosting(true);
    try {
      const commentPayload = {
        body: data.body,
        attachments: media?.id ? [media?.id] : [],
      };
      const updatedComment = await updateComment(`${editingComment.id}`, commentPayload);

      const updatedComments = post.comments.map((comment) => {
        if (comment.id === editingComment.id) {
          return { ...comment, ...updatedComment };
        }
        if (comment.comments) {
          return {
            ...comment,
            comments: comment.comments.map((subComment) => (
              subComment.id === editingComment.id
                ? { ...subComment, ...updatedComment }
                : subComment
            )),
          };
        }
        return comment;
      });

      setPost({ ...post, comments: updatedComments });
      if (updatedComment?.post_id) {
        dispatch(updatePostComments({ action: 'update', comment: updatedComment }));
      }
      if (updatedComment?.comment_id) {
        dispatch(updatePostSubComments({ action: 'update', comment: updatedComment }));
      }
      setEditingComment(null);
      reset({ body: '' });
    } catch (e) {
      console.error('Error updating comment:', e);
      toast.error('Failed to update comment');
    }
    setIsPosting(false);
    handleResetMedia();
  };

  const onSubmit = async (data) => {
    if (isPosting) {
      return;
    }

    if (editingComment) {
      handleEditSubmit(data);
      return;
    }

    reset({ body: '' });
    if (replyTo.type === 'post') {
      const commentPayload = {
        ...data,
        post_id: post.id,
        author_id: user.id,
        attachments: media?.id ? [media?.id] : [],
      };
      setIsPosting(true);
      const newComment = await postComment(commentPayload);
      const newCommentState = {
        ...newComment,
        numberOfLikes: 0,
        author: {
          first_name: user.first_name,
          last_name: user.last_name,
          role: user?.role,
          roles: user?.roles,
          profile_image: user.profile_image,
        },
        comments: [],
      };
      setPost((prev) => ({
        ...prev,
        comments: [
          ...prev.comments,
          newCommentState,
        ],
      }));
      dispatch(updatePostComments({ action: 'add', comment: newCommentState }));
    } else {
      const commentPayload = {
        ...data,
        comment_id: replyTo?.type === 'subComment' ? replyTo?.topLevelComment?.id : replyTo?.data?.id,
        author_id: user.id,
        attachments: media?.id ? [media?.id] : [],
      };
      const newComment = await postComment(commentPayload);
      const newCommentState = {
        ...newComment,
        numberOfLikes: 0,
        author: {
          first_name: user?.first_name,
          last_name: user.last_name,
          role: user?.role,
          roles: user?.roles,
          profile_image: user?.profile_image,
        },
      };
      const updatedComments = post.comments.map((comment) => {
        if (comment.id === newComment.comment_id) {
          const tempComment = { ...comment };
          if (tempComment?.comments) {
            tempComment.comments = [
              ...tempComment.comments,
              newCommentState,
            ];
          } else {
            tempComment.comments = [
              newCommentState,
            ];
          }
          return tempComment;
        }
        return comment;
      });
      setPost({ ...post, comments: updatedComments });
      dispatch(updatePostSubComments({ action: 'add', comment: newCommentState }));
    }
    setReplyTo({ data: post, type: 'post' });
    handleResetMedia();
  };

  // Scrolls the textarea to the bottom when a scrollable media is added
  const handleScrollableMedia = () => {
    const textarea = document.querySelector('.comment-input .native-textarea');
    if (textarea) {
      textarea.scrollTop = textarea.scrollHeight;
    }
  };

  const handlePhoto = async () => {
    if (loading) {
      return;
    }

    try {
      const { media: photo, webviewPath } = await takePhoto();
      console.log('Create Comment ~ takePhoto response: ', { photo, webviewPath });
      if (photo) {
        setMedia({ webviewPath, id: photo.id, type: 'photo' });
      }
      handleScrollableMedia();
      setIsVideo(false);
    } catch (e) {
      if (e.message !== nonTerminalErrorMessages.cancelledPhotosApp) {
        console.error(e);
      }
    }
  };

  const handleWebVideo = async () => {
    const videoObj = await takeWebVideo();
    if (videoObj?.media) {
      const { media: video } = videoObj;
      setMedia({ webviewPath: video.uri, id: video.id, type: 'video' });
      setIsVideo(true);
    }
  };

  const handleMobileVideo = async () => {
    const videoObj = await takeVideo();
    if (videoObj?.media) {
      const { media: video, webviewPath } = videoObj;
      setMedia({ webviewPath, id: video.id, type: 'video' });
      setIsVideo(true);
    }
  };

  const handleVideo = async () => {
    if (loading) {
      return;
    }
    const uploader = platform?.isNativePlatform ? handleMobileVideo : handleWebVideo;

    try {
      await uploader();
      handleScrollableMedia();
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  const handlePdf = async () => {
    if (loading) {
      return;
    }
    try {
      const pdfObj = await takePdf();
      if (pdfObj?.media) {
        const { webviewPath, media: pdf } = pdfObj;
        setMedia({ webviewPath, id: pdf.id, type: 'pdfs' });
        setActionSheetResult(null);
      }
      handleScrollableMedia();
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  const handleAction = (action) => {
    switch (action) {
      case 'photo':
        handlePhoto();
        break;
      case 'video':
        handleVideo();
        break;
      default:
        resetReplyTo();
    }
  };

  const presentActionSheet = (buttons) => {
    present({
      buttons,
      onDidDismiss: ({ detail }) => setActionSheetResult(detail),
    });
  };

  const handlePresentActionSheet = () => {
    const actionSheetButtons = getMediaActionButtons();
    presentActionSheet(actionSheetButtons);
  };

  const renderReplyToName = () => {
    const author = replyTo?.data?.author;
    const authorId = replyTo?.data?.author_id;
    const userId = user?.id;

    if (author?.role === 'admin') {
      return comments.coachLubav;
    }
    if (!author?.first_name) {
      return authorId === userId ? comments.me : `${comments.user} ${authorId}`;
    }
    return displayName(author.first_name, author?.last_name);
  };

  useEffect(() => {
    if (actionSheetResult?.data) {
      const {
        data: { action },
      } = actionSheetResult;
      handleAction(action);
    }
  }, [actionSheetResult]);

  // if feedFilters is empty => if user enters app from notifications instead of feed
  const getFeedFilters = () => {
    const itemMeta = async () => {
      try {
        const filterData = await get('/user-feed/filters');
        if (filterData) {
          dispatch(updateFeedFilters(filterData));
        }
      } catch (e) {
        console.error('Error getting feed filters in post page: ', e);
      }
    };
    itemMeta();
  };

  const onBack = () => {
    dispatch(setShouldRestoreFeedPosition(true));
    history.goBack();
  };

  useEffect(() => {
    if (user && post) {
      if (!feedFilters) {
        getFeedFilters();
      }
    }
  }, [user, post]);

  if (!post) {
    return (
      <IonPage>
        <IonContent />
      </IonPage>
    );
  }

  return (
    <PageWithBackButton backButtonText="Comments" onBack={onBack}>
      <IonContent>
        <Post
          post={post}
          setPost={setPost}
          filterMeta={feedFilters}
          hideComments
        />
        <div style={{ marginBottom: '16px' }}>
          {post?.comments?.length > 0
            && post?.comments.map((comment) => (
              <Comment
                key={comment.id}
                comment={comment}
                setReplyTo={setReplyTo}
                deleteComment={deleteSingleComment}
                resetReplyTo={resetReplyTo}
                reportComment={reportComment}
                editComment={handleEditComment}
                expand
                isAllowedToComment={isAllowedToComment}
              />
            ))}
        </div>
      </IonContent>
      <IonFooter style={{ backgroundColor: container.white }}>
        {isAllowedToComment && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="comment-reply">
              <Typography className="reply-to">
                {editingComment ? 'Editing comment' : `${comments.replyingTo} ${renderReplyToName()}`}
              </Typography>
              <BackrsButton
                label={editingComment ? comments.update : comments.post}
                onClick={handleSubmit(onSubmit)}
                isDisabled={isDisabled}
                disableRipple
                buttonStylesOverride={postBtnStyle}
              />
            </div>
            <div className="comment-input-container">
              <SendIconAvatar user={user} />
              <div className="comment-input-wrapper">
                <div className="textarea-container">
                  <Controller
                    render={({ field: { onChange, value } }) => (
                      <IonTextarea
                        className="comment-input"
                        placeholder="Add your comment"
                        onIonChange={(e) => {
                          onChange(e);
                          const textarea = e.target;
                          const isScrollable = textarea.scrollHeight > textarea.clientHeight;
                          textarea.style.setProperty('--is-scrollable', isScrollable ? '1' : '0');
                        }}
                        value={value}
                        rows={1}
                        autoGrow
                        required
                        spellcheck
                        style={{
                          ...textAreaStyles,
                          '--has-media': (media?.webviewPath || loading) ? '1' : '0',
                          '--is-scrollable': '0',
                        }}
                        autocapitalize="on"
                        enterkeyhint="enter"
                        disabled={!isAllowedToComment}
                      />
                    )}
                    control={control}
                    name="body"
                  />
                  <Box>
                    {error && (
                      <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
                        {displayErrorText(error)}
                      </Alert>
                    )}
                  </Box>
                  {(media?.webviewPath || loading) && !error && (
                    <IonItem
                      detail={false}
                      lines="none"
                      className="post-option-item post-media-preview"
                      style={{ marginLeft: '0.625rem', lineHeight: 'normal' }}
                    >
                      {loading && (
                        <Box sx={{ paddingRight: '.75rem' }}>
                          <CircularProgress size={20} />
                        </Box>
                      )}
                      {!loading && !isVideo && media?.webviewPath && media?.type !== 'pdfs' && (
                        <IonThumbnail slot="start" className="post-photo-thumbnail">
                          <img src={media?.webviewPath} alt="current comment" style={{ borderRadius: '8px' }} />
                        </IonThumbnail>
                      )}
                      {!loading && isVideo && media?.webviewPath && (
                        <IonIcon
                          style={{ marginRight: '1rem', color: 'inherit' }}
                          icon={videocam}
                        />
                      )}
                      <IonLabel>
                        {displayAttachmentLabel(loading, media, true, width)}
                      </IonLabel>
                      <IonButton fill="clear" onClick={() => handleMediaClose()}>
                        <IonIcon style={{ color: 'grey' }} slot="icon-only" icon={closeCircle} />
                      </IonButton>
                    </IonItem>
                  )}
                </div>
                <Stack sx={iconContainer}>
                  <IonItem
                    button
                    detail={false}
                    lines="none"
                    className="post-option-item"
                    onClick={handlePresentActionSheet}
                    style={mediaIcon}
                    disabled={!isAllowedToComment}
                  >
                    <IonIcon
                      style={{
                        color: text.navy,
                        fontSize: '20px',
                      }}
                      src="assets/icon/addPhoto.svg"
                    />
                  </IonItem>
                  <IonItem
                    button
                    detail={false}
                    lines="none"
                    className="post-option-item"
                    onClick={handlePdf}
                    style={mediaIcon}
                    disabled={!isAllowedToComment}
                  >
                    <IonIcon
                      style={{
                        color: text.navy,
                        fontSize: '24px',
                      }}
                      src="assets/icon/file-pdf.svg"
                    />
                  </IonItem>
                </Stack>
              </div>
            </div>
          </form>
        )}
      </IonFooter>
    </PageWithBackButton>
  );
};

export default Comments;
